<script>
import options from "./LoginOverlay.options"

export default options
</script>

<template>
    <transition 
        name="nyk-transition-fade-scale-up" 
        appear
        @after-enter="onOverlayEntered"
        @after-leave="onOverlayLeft"
    >
        <div v-if="open" 
            data-submenu="login-overlay"
            id="submenu-login-overlay"
            class="nyk-login-overlay">
            <!-- Language Bar -->
            <ul class="nyk-js--submenu-list nyk-menu__top-menu__top__menu-items nyk-login-overlay__language-selector" :aria-label="languageBarAriaLabel">
                <li v-for="language in languageItems" :key="language.languageCode"
                    class="nyk-menu__top-menu__top__menu-items__item">
                        <a href="#"
                    @click="switchLanguage(language.languageCode)"
                    @keydown.prevent.tab="navigateForwardInSubmenu"
                    @keydown.prevent.tab.shift="navigateBackInSubmenu"
                    @keydown.prevent.down="navigateForwardInSubmenu"
                    @keydown.prevent.up="navigateBackInSubmenu"
                    @keydown.prevent.left="navigateToPreviousListInSubmenu"
                    @keydown.prevent.right="navigateToNextListInSubmenu"
                    @keydown.prevent.esc="closeSubmenu"
                    class="nyk-js--submenu-item nyk-js--menu-item nyk-menu__top-menu__top__menu-items__item__link"
                    :class="{ 'nyk-menu__top-menu__top__menu-items__item__link--active': currentLocale === language.languageCode }"
                    :aria-current="currentLocale === language.languageCode ? 'true' : undefined"
                            :aria-label="getLanguageSwitchAriaLabel(language)">
                        {{ getLanguageTitle(language) }}
                    </a>
                </li>
            </ul>

            <!-- Notifications -->
            <div v-show="notifications && notifications.length > 0" 
                class="nyk-login-overlay__notifications"
                role="region"
                :aria-label="notificationsAriaLabel">
                <transition-group name="nyk-transition-slide-down" tag="div">
                    <nyk-alert
                        v-for="(notification, index) in visibleNotifications"
                        :key="index"
                        type="info-neutral"
                        role="alert"
                        aria-live="assertive"
                        aria-atomic="false"
                        class="nyk-login-overlay__notification"
                        :aria-labelledby="`notification-title-${index}`"
                        :aria-describedby="`notification-body-${index}`">
                        <div class="nyk-login-overlay__notification-body">
                            <strong :id="`notification-title-${index}`" role="term">{{ getNotificationTypeLabel(notification) }}:</strong>
                            <div :id="`notification-body-${index}`" role="definition" v-html="getNotificationBody(notification)"></div>
                        </div>
                    </nyk-alert>
                </transition-group>
            </div>

            <div v-if="bannerImageUrl || (cards && cards.length > 0)" class="nyk-login-overlay__content">
                <div v-if="bannerImageUrl" class="nyk-image nyk-image--no-margin nyk-image--no-padding">
                    <div class="nyk-image__content">
                        <div class="nyk-image__image-content">
                            <picture>
                                <img :src="bannerImageUrl"
                                    :alt="altText" 
                                    class="nyk-image__content__img">
                            </picture>
                        </div>
                    </div>
                </div>

                <ul v-if="cards && cards.length > 0" class="nyk-js--submenu-list nyk-login-overlay__gallery" :aria-label="quickLinksAriaLabel">
                    <li v-for="(card, index) in cards" 
                        :key="index"
                        class="nyk-card-medium" :class="`nyk-card-medium--color-${card.variant || 'primary'}`">
                        <div class="nyk-card-medium__header">
                            <h3 class="nyk-card-medium__header__title">{{ getCardHeadline(card) }}</h3>
                        </div>

                        <div class="nyk-card-medium__footer">
                            <a class="nyk-js--submenu-item nyk-card-medium__footer__link" 
                                :href="getCardHref(card)"
                                :target="card.target"
                                :rel="card.target === '_blank' ? 'noopener noreferrer' : undefined"
                                @keydown.prevent.tab="navigateForwardInSubmenu"
                                @keydown.prevent.tab.shift="navigateBackInSubmenu"
                                @keydown.prevent.down="navigateForwardInSubmenu"
                                @keydown.prevent.up="navigateBackInSubmenu"
                                @keydown.prevent.left="navigateToPreviousListInSubmenu"
                                @keydown.prevent.right="navigateToNextListInSubmenu"
                                @keydown.prevent.esc="closeSubmenu"
                                :aria-label="getCardHeadline(card)">
                                <span class="nyk-card-medium__footer__text">
                                    {{ getCardFooterText(card) }}
                                </span>
                                
                                <h3 class="nyk-card-medium__footer__header nyk-card-medium__header__title">{{ getCardHeadline(card) }}</h3>

                                <div class="nyk-card-medium__footer__link__button nyk-button nyk-button--link-circle-arrow right">
                                    <div class="nyk-button__icon">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 12" role="presentation" aria-hidden="true">
                                            <path fill="var(--colortheme-icon)" d="M0 5.393v1.231h11.37l-3.898 4.243.67.633L14 5.991 8.142.5l-.67.633 3.898 4.26H0Z"></path>
                                        </svg>
                                    </div>
                                </div>
                            </a>
                        </div>
                    </li>
                </ul>
            </div>

            <!-- Footer Section -->
            <footer class="nyk-login-overlay__footer" :aria-label="footerAriaLabel">
                <h2 id="nyk-login-overlay-footer-title" class="nyk-login-overlay__footer__title">{{ footerTitle }}</h2>

                <ul class="nyk-js--submenu-list nyk-login-overlay__footer__list nyk-menu__submenu__content__items__group__list" aria-labelledby="nyk-login-overlay-footer-title">
                    <li class="nyk-menu__submenu__content__items__group__list__item" 
                        v-for="(item, index) in footerItems"
                        :key="index">
                        <a class="nyk-js--submenu-item nyk-button nyk-button--link-menu" 
                        :href="getFooterItemHref(item)" 
                        @keydown.prevent.tab="navigateForwardInSubmenu"
                        @keydown.prevent.tab.shift="navigateBackInSubmenu"
                        @keydown.prevent.down="navigateForwardInSubmenu"
                        @keydown.prevent.up="navigateBackInSubmenu"
                        @keydown.prevent.left="navigateToPreviousListInSubmenu"
                        @keydown.prevent.right="navigateToNextListInSubmenu"
                        @keydown.prevent.esc="closeSubmenu"
                        :target="isTelLink(item) ? undefined : item.target"
                        :rel="item.target === '_blank' && !isTelLink(item) ? 'noopener noreferrer' : undefined">
                            <div v-html="getFooterItemLabel(item)"></div>
                            <div v-if="!isTelLink(item)" class="nyk-button__icon">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 12" role="presentation"
                                    aria-hidden="true">
                                    <path fill="var(--colortheme-icon)"
                                        d="M0 5.393v1.231h11.37l-3.898 4.243.67.633L14 5.991 8.142.5l-.67.633 3.898 4.26H0Z">
                                    </path>
                                </svg>
                            </div>
                        </a>
                    </li>
                </ul>
            </footer>
        </div>
    </transition>
</template>